import axios, { AxiosError } from "axios";
import {
  useQuery,
  UseQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "react-query";
import {
  AttendanceException,
  AttendanceExceptionListResponse,
} from "../model/AttendanceException";
import {
  AttendanceExceptionsRequest,
  ExceptionCountRequest,
  ResolveExceptionRequest,
} from "../request/AttendanceExceptionsRequest";
import { HttpClientError } from "../../../../providers/HttpClient";
import { invalidateAttendanceViews } from "./attendanceCache";

const ATTENDANCE_EXCEPTIONS_QUERY_KEY = "AttendanceExceptionsApiService";
const EXCEPTION_COUNT_QUERY_KEY = "ExceptionCountApiService";

const buildListParams = (
  request: AttendanceExceptionsRequest
): Record<string, string | number | boolean> => ({
  ...(request.page && { page: request.page }),
  ...(request.page_size && { page_size: request.page_size }),
  ...(request.shift_location && { shift_location: request.shift_location }),
  ...(request.shift_site && { shift_site: request.shift_site }),
  ...(request.exception_type && { exception_type: request.exception_type }),
  ...(request.status && { status: request.status }),
  ...(request.date && { date: request.date }),
  ...(request.date_from && { date_from: request.date_from }),
  ...(request.date_to && { date_to: request.date_to }),
  ...(request.search && { search: request.search }),
  ...(request.include_supervisor_clock !== undefined && {
    include_supervisor_clock: request.include_supervisor_clock,
  }),
});

const AttendanceExceptionsApiService = async (
  request: AttendanceExceptionsRequest
): Promise<AttendanceExceptionListResponse> => {
  try {
    const { data } = await axios.get("/attendance-exceptions/", {
      params: buildListParams(request),
    });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);

    customError.cause = axiosError;
    throw customError;
  }
};

const useAttendanceExceptionsApiService = (
  request: AttendanceExceptionsRequest
): UseQueryResult<AttendanceExceptionListResponse, Error | AxiosError> => {
  const { enabled = true, ...queryParams } = request;
  const normalizedParams = {
    page: queryParams.page ?? null,
    page_size: queryParams.page_size ?? null,
    shift_location: queryParams.shift_location ?? null,
    shift_site: queryParams.shift_site ?? null,
    exception_type: queryParams.exception_type ?? null,
    status: queryParams.status ?? null,
    date: queryParams.date ?? null,
    date_from: queryParams.date_from ?? null,
    date_to: queryParams.date_to ?? null,
    search: queryParams.search ?? null,
    include_supervisor_clock: queryParams.include_supervisor_clock ?? null,
  };
  return useQuery<AttendanceExceptionListResponse, Error | AxiosError>(
    [ATTENDANCE_EXCEPTIONS_QUERY_KEY, ...Object.values(normalizedParams)],
    () => AttendanceExceptionsApiService(queryParams),
    { keepPreviousData: true, enabled }
  );
};

/**
 * Fetches all exceptions for export (large page_size).
 * CSV is generated client-side from the JSON response.
 */
const ExportExceptionsApiService = async (
  request: AttendanceExceptionsRequest
): Promise<AttendanceExceptionListResponse> => {
  try {
    const { data } = await axios.get("/attendance-exceptions/", {
      params: {
        page: 1,
        page_size: 10000,
        ...buildListParams({
          ...request,
          page: undefined,
          page_size: undefined,
        }),
      },
    });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);

    customError.cause = axiosError;
    throw customError;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const useExportExceptionsApiService = () =>
  useMutation<
    AttendanceExceptionListResponse,
    AxiosError,
    AttendanceExceptionsRequest
  >((request) => ExportExceptionsApiService(request), {
    mutationKey: ["ExportExceptionsApiService"],
  });

const ResolveExceptionApiService = async (
  id: string,
  body: ResolveExceptionRequest
): Promise<AttendanceException> => {
  try {
    const { data } = await axios.post(
      `/attendance-exceptions/${encodeURIComponent(id)}/resolve/`,
      body
    );
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);

    customError.cause = axiosError;
    throw customError;
  }
};

const useResolveExceptionApiService = (): UseMutationResult<
  AttendanceException,
  AxiosError | Error,
  { id: string; body: ResolveExceptionRequest }
> => {
  const queryClient = useQueryClient();
  return useMutation<
    AttendanceException,
    AxiosError | Error,
    { id: string; body: ResolveExceptionRequest }
  >(({ id, body }) => ResolveExceptionApiService(id, body), {
    mutationKey: ["ResolveExceptionApiService"],
    onSuccess: () => {
      queryClient.invalidateQueries([ATTENDANCE_EXCEPTIONS_QUERY_KEY]);
      queryClient.invalidateQueries([EXCEPTION_COUNT_QUERY_KEY]);
      // The overview hierarchy's per-site/region Exceptions column reads
      // exception_summary from this query — refetch so the count clears.
      queryClient.invalidateQueries(["ShiftSitesOverviewApiService"]);
      // Resolve actions (geofence clock_override, clock_in, no_show) mutate the
      // employee's clock state, so refresh the attendance views too.
      invalidateAttendanceViews(queryClient);
    },
  });
};

const ReopenExceptionApiService = async (
  id: string
): Promise<AttendanceException> => {
  try {
    const { data } = await axios.post(
      `/attendance-exceptions/${encodeURIComponent(id)}/reopen/`,
      {}
    );
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);

    customError.cause = axiosError;
    throw customError;
  }
};

const useReopenExceptionApiService = (): UseMutationResult<
  AttendanceException,
  AxiosError | Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation<AttendanceException, AxiosError | Error, string>(
    (id) => ReopenExceptionApiService(id),
    {
      mutationKey: ["ReopenExceptionApiService"],
      onSuccess: () => {
        queryClient.invalidateQueries([ATTENDANCE_EXCEPTIONS_QUERY_KEY]);
        queryClient.invalidateQueries([EXCEPTION_COUNT_QUERY_KEY]);
        // Reopening bumps the overview's per-site/region Exceptions count back
        // up — refetch the hierarchy so the column reflects it.
        queryClient.invalidateQueries(["ShiftSitesOverviewApiService"]);
        // Reopening can revert a resolution's clock effect, so refresh the
        // attendance views too.
        invalidateAttendanceViews(queryClient);
      },
    }
  );
};

interface ExceptionCountResponse {
  count: number;
}

const ExceptionCountApiService = async (
  request: ExceptionCountRequest
): Promise<ExceptionCountResponse> => {
  try {
    const { data } = await axios.get("/attendance-exceptions/count/", {
      params: {
        ...(request.shift_location && {
          shift_location: request.shift_location,
        }),
        ...(request.shift_site && { shift_site: request.shift_site }),
        ...(request.status && { status: request.status }),
        ...(request.include_supervisor_clock !== undefined && {
          include_supervisor_clock: request.include_supervisor_clock,
        }),
        ...(request.date && { date: request.date }),
        ...(request.date_from && { date_from: request.date_from }),
        ...(request.date_to && { date_to: request.date_to }),
      },
    });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);

    customError.cause = axiosError;
    throw customError;
  }
};

const useExceptionCountApiService = (
  request: ExceptionCountRequest
): UseQueryResult<ExceptionCountResponse, Error | AxiosError> => {
  const normalizedParams = {
    shift_location: request.shift_location ?? null,
    shift_site: request.shift_site ?? null,
    status: request.status ?? "open",
    include_supervisor_clock: request.include_supervisor_clock ?? null,
    date: request.date ?? null,
    date_from: request.date_from ?? null,
    date_to: request.date_to ?? null,
  };
  return useQuery<ExceptionCountResponse, Error | AxiosError>(
    [EXCEPTION_COUNT_QUERY_KEY, ...Object.values(normalizedParams)],
    () =>
      ExceptionCountApiService({
        ...request,
        status: request.status ?? "open",
      }),
    { keepPreviousData: true, refetchOnWindowFocus: true }
  );
};

export {
  AttendanceExceptionsApiService,
  useAttendanceExceptionsApiService,
  useExportExceptionsApiService,
  useResolveExceptionApiService,
  useReopenExceptionApiService,
  useExceptionCountApiService,
};
export type { ExceptionCountResponse };
